import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Plus, Bell, Check, CreditCard, PenSquare, BellRing } from "lucide-react";
import { enablePush, testPush } from "@/lib/push";
import { toast } from "sonner";

export default function PianiRatePage() {
  const { user } = useAuth();
  const [piani, setPiani] = useState([]);
  const [studenti, setStudenti] = useState([]);
  const [corsi, setCorsi] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ studente_id:"", corso_id:"", importo_totale:0, acconto:0, numero_rate:6, prima_scadenza:"" });
  const [payDlg, setPayDlg] = useState(null); // {piano_id, rata_id, dovuto, importo_pagato}
  const [editRata, setEditRata] = useState(null); // {piano_id, rata_id, importo, scadenza}
  const admin = user?.role === "admin";

  const load = async () => {
    const promises = [api.get("/piani-rate")];
    if (admin) {
      promises.push(api.get("/users?role=studente"), api.get("/corsi"));
    }
    const results = await Promise.all(promises);
    setPiani(results[0].data);
    if (admin) { setStudenti(results[1].data); setCorsi(results[2].data); }
  };
  useEffect(() => { load(); }, []);

  const genRate = () => {
    const rimanente = +form.importo_totale - +form.acconto;
    const importoRata = +(rimanente / +form.numero_rate).toFixed(2);
    const start = new Date(form.prima_scadenza);
    const rate = [];
    for (let i=0; i<+form.numero_rate; i++) {
      const d = new Date(start); d.setMonth(d.getMonth()+i);
      rate.push({ numero:i+1, importo:importoRata, scadenza:d.toISOString().slice(0,10), pagata:false });
    }
    return rate;
  };
  const save = async () => {
    try {
      await api.post("/piani-rate", { ...form, importo_totale:+form.importo_totale, acconto:+form.acconto, rate:genRate() });
      toast.success("Piano creato"); setOpen(false); load();
    } catch { toast.error("Errore"); }
  };
  const paga = async () => {
    try {
      const { data } = await api.post(`/piani-rate/${payDlg.piano_id}/rate/${payDlg.rata_id}/paga`, { importo_pagato: +payDlg.importo_pagato });
      if (data.eccedenza > 0) toast.success(`Rata pagata. Eccedenza € ${data.eccedenza.toFixed(2)} ridistribuita sulle rate future`);
      else toast.success("Rata segnata pagata");
      setPayDlg(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const salvaEditRata = async () => {
    try {
      await api.put(`/piani-rate/${editRata.piano_id}/rate/${editRata.rata_id}`, { importo: +editRata.importo, scadenza: editRata.scadenza });
      toast.success("Rata aggiornata"); setEditRata(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };
  const sollecita = async (piano_id, rata_id) => {
    await api.post(`/piani-rate/${piano_id}/sollecita/${rata_id}`);
    toast.success("Sollecito inviato"); load();
  };
  const attivaPush = async () => {
    const r = await enablePush();
    if (r.ok) toast.success("Notifiche push attivate"); else toast.error(`Impossibile: ${r.reason}`);
  };

  const today = new Date().toISOString().slice(0,10);
  return (
    <div className="p-4 sm:p-8">
      <div className="flex flex-wrap gap-4 items-center justify-between mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#F7941E] mb-2">Amministrazione</div>
          <h1 className="text-3xl font-bold text-stone-900">Piani Rate</h1>
          <p className="text-stone-600 mt-1">Rateizzazioni personalizzabili · Solleciti automatici</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={attivaPush} className="border-[#1F4E79] text-[#1F4E79]" data-testid="enable-push-btn"><BellRing className="w-4 h-4 mr-2"/>Attiva Notifiche</Button>
          {admin && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild><Button className="bg-[#F7941E] hover:bg-[#E08316] text-white" data-testid="new-piano-btn"><Plus className="w-4 h-4 mr-2"/>Nuovo Piano</Button></DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Nuovo Piano Rateale</DialogTitle><DialogDescription>Definisci studente, corso, importo, acconto e scadenze delle rate.</DialogDescription></DialogHeader>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="col-span-2"><Label>Studente</Label>
                  <Select value={form.studente_id} onValueChange={v=>{
                    const s = studenti.find(x=>x.id===v);
                    const updates = { studente_id: v };
                    if (s?.prezzo_personalizzato) updates.importo_totale = s.prezzo_personalizzato;
                    setForm({...form, ...updates});
                  }}>
                    <SelectTrigger data-testid="piano-studente-select"><SelectValue placeholder="Seleziona"/></SelectTrigger>
                    <SelectContent>{studenti.map(s=><SelectItem key={s.id} value={s.id}>{s.name}{s.prezzo_personalizzato ? ` (€ ${s.prezzo_personalizzato})` : ""}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="col-span-2"><Label>Corso</Label>
                  <Select value={form.corso_id} onValueChange={v=>{ const c = corsi.find(x=>x.id===v); setForm({...form,corso_id:v, importo_totale:c?.prezzo || 0}); }}>
                    <SelectTrigger><SelectValue placeholder="Seleziona"/></SelectTrigger>
                    <SelectContent>{corsi.map(c=><SelectItem key={c.id} value={c.id}>{c.titolo}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Importo totale €</Label><Input type="number" value={form.importo_totale} onChange={e=>setForm({...form,importo_totale:e.target.value})}/></div>
                <div><Label>Acconto €</Label><Input type="number" value={form.acconto} onChange={e=>setForm({...form,acconto:e.target.value})}/></div>
                <div><Label>Numero rate</Label><Input type="number" value={form.numero_rate} onChange={e=>setForm({...form,numero_rate:e.target.value})}/></div>
                <div><Label>Prima scadenza</Label><Input type="date" value={form.prima_scadenza} onChange={e=>setForm({...form,prima_scadenza:e.target.value})}/></div>
              </div>
              <DialogFooter><Button onClick={save} className="bg-[#F7941E] hover:bg-[#E08316] text-white" data-testid="piano-save-btn">Crea Piano</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        )}
        </div>
      </div>

      <div className="space-y-4">
        {piani.map(p => {
          const stud = studenti.find(s=>s.id===p.studente_id);
          const corso = corsi.find(c=>c.id===p.corso_id);
          return (
            <Card key={p.id} className="p-5 bg-white border-[#E2E8F0]" data-testid={`piano-card-${p.id}`}>
              <div className="flex justify-between mb-4">
                <div>
                  <div className="font-semibold text-stone-900">{stud?.name || user?.name || "Allievo"}</div>
                  <div className="text-sm text-stone-600">{corso?.titolo}</div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-[#F7941E]">€ {p.importo_totale.toFixed(2)}</div>
                  <div className="text-xs text-stone-500">Acconto: € {p.acconto.toFixed(2)}</div>
                </div>
              </div>
              <Table>
                <TableHeader><TableRow><TableHead>N.</TableHead><TableHead>Importo</TableHead><TableHead>Scadenza</TableHead><TableHead>Stato</TableHead><TableHead className="text-right">Azioni</TableHead></TableRow></TableHeader>
                <TableBody>
                  {p.rate.map(r => {
                    const scaduta = r.scadenza < today && !r.pagata;
                    return (
                      <TableRow key={r.id}>
                        <TableCell>{r.numero}</TableCell>
                        <TableCell className="font-mono">
                          € {(r.pagata && r.importo_pagato != null ? r.importo_pagato : r.importo).toFixed(2)}
                          {r.pagata && r.importo_pagato != null && Math.abs(r.importo_pagato - r.importo) > 0.001 && (
                            <span className="text-xs text-stone-400 ml-1">(prev. € {r.importo.toFixed(2)})</span>
                          )}
                        </TableCell>
                        <TableCell>{r.scadenza}</TableCell>
                        <TableCell>
                          {r.pagata ? <Badge className="bg-emerald-50 text-emerald-800">Pagata</Badge>
                          : scaduta ? <Badge className="bg-red-50 text-red-800">Insoluta</Badge>
                          : <Badge className="bg-amber-50 text-amber-900">In scadenza</Badge>}
                          {r.pagata && r.registrato_da_nome && <div className="text-xs text-stone-400 mt-0.5">da {r.registrato_da_nome}</div>}
                        </TableCell>
                        <TableCell className="text-right space-x-1">
                          {r.pagata && <span className="text-xs text-emerald-700 font-medium">Pagata</span>}
                          {admin && !r.pagata && <>
                            <Button size="sm" variant="outline" title="Modifica importo/scadenza" onClick={()=>setEditRata({ piano_id:p.id, rata_id:r.id, importo:r.importo, scadenza:r.scadenza })} data-testid={`edit-rata-${r.id}`}><PenSquare className="w-3 h-3"/></Button>
                            <Button size="sm" variant="outline" title="Segna pagata" onClick={()=>setPayDlg({ piano_id:p.id, rata_id:r.id, dovuto:r.importo, importo_pagato:r.importo })} data-testid={`paga-rata-${r.id}`}><Check className="w-3 h-3"/></Button>
                            {scaduta && <Button size="sm" variant="outline" className="border-red-300 text-red-600" onClick={()=>sollecita(p.id, r.id)} data-testid={`sollecita-rata-${r.id}`}><Bell className="w-3 h-3"/></Button>}
                          </>}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Card>
          );
        })}
        {piani.length===0 && <div className="text-stone-500 text-center py-12">Nessun piano rateale.</div>}
      </div>

      <Dialog open={!!payDlg} onOpenChange={(o)=>{if(!o) setPayDlg(null);}}>
        <DialogContent>
          <DialogHeader><DialogTitle>Registra pagamento rata</DialogTitle><DialogDescription>Se l'importo pagato supera il dovuto, l'eccedenza riduce automaticamente le rate future (stesso numero di rate).</DialogDescription></DialogHeader>
          {payDlg && (
            <div className="grid gap-4">
              <div className="text-sm text-stone-600">Importo dovuto: <span className="font-mono font-semibold">€ {payDlg.dovuto.toFixed(2)}</span></div>
              <div><Label>Importo pagato €</Label><Input type="number" step="0.01" value={payDlg.importo_pagato} onChange={e=>setPayDlg({...payDlg, importo_pagato:e.target.value})} data-testid="pay-importo-input"/></div>
            </div>
          )}
          <DialogFooter><Button onClick={paga} className="bg-[#F7941E] hover:bg-[#E08316] text-white" data-testid="pay-confirm-btn">Conferma pagamento</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editRata} onOpenChange={(o)=>{if(!o) setEditRata(null);}}>
        <DialogContent>
          <DialogHeader><DialogTitle>Modifica rata</DialogTitle><DialogDescription>Aggiorna importo e scadenza della rata.</DialogDescription></DialogHeader>
          {editRata && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><Label>Importo €</Label><Input type="number" step="0.01" value={editRata.importo} onChange={e=>setEditRata({...editRata, importo:e.target.value})} data-testid="edit-rata-importo"/></div>
              <div><Label>Scadenza</Label><Input type="date" value={editRata.scadenza} onChange={e=>setEditRata({...editRata, scadenza:e.target.value})} data-testid="edit-rata-scadenza"/></div>
            </div>
          )}
          <DialogFooter><Button onClick={salvaEditRata} className="bg-[#F7941E] hover:bg-[#E08316] text-white" data-testid="edit-rata-save">Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
